import { useMemo } from 'react';
import { TimeSeriesValueTuple } from '@/core';

export interface SeriesWithLabels {
  name: string;
  formattedName?: string;
  values: TimeSeriesValueTuple[];
  labels?: Record<string, string>;
}

export interface GroupedSeriesRow {
  name: string;
  formattedName?: string;
  labels: Record<string, string>;
  values: TimeSeriesValueTuple[];
  cellStates: Array<string | undefined>;
}

function getLabelEntries(labels: Record<string, string> | undefined, colorBy: string) {
  if (!labels) return [];
  return Object.entries(labels)
    .filter(([k]) => k !== colorBy)
    .sort(([a], [b]) => a.localeCompare(b));
}

/**
 * Grouping key that includes both label keys and values (e.g. "app=web / env=prod")
 * so series with different label schemas but overlapping values are never merged.
 */
function getGroupKey(labels: Record<string, string> | undefined, colorBy: string, seriesName: string): string {
  const entries = getLabelEntries(labels, colorBy);
  if (entries.length === 0) return seriesName;
  return entries.map(([k, v]) => `${k}=${v}`).join(' / ');
}

/** Human-readable display name using only label values (e.g. "web / prod"). */
function getGroupDisplayName(labels: Record<string, string> | undefined, colorBy: string, seriesName: string): string {
  const entries = getLabelEntries(labels, colorBy);
  if (entries.length === 0) return seriesName;
  return entries.map(([, v]) => v).join(' / ');
}

/**
 * Groups flat time series by all labels except `colorBy` and extracts per-cell
 * color state from the `colorBy` label. This enables per-cell label-based
 * coloring in the StatusHistory panel, similar to Grafana's "Grouping to
 * matrix" transform.
 *
 * At each timestamp, finds which series in the group has a non-null value
 * and reads the `colorBy` label from that series as the cell state.
 *
 * When `statePriority` is provided, series within each group are sorted so
 * that higher-priority states (earlier in the array) win when multiple series
 * have non-null values at the same timestamp.
 */
export function groupSeries(series: SeriesWithLabels[], colorBy: string, statePriority?: string[]): GroupedSeriesRow[] {
  const groups = new Map<string, { displayName: string; seriesList: SeriesWithLabels[] }>();

  for (const s of series) {
    const groupKey = getGroupKey(s.labels, colorBy, s.name);
    const existing = groups.get(groupKey);
    if (existing) {
      existing.seriesList.push(s);
    } else {
      const displayName = getGroupDisplayName(s.labels, colorBy, s.name);
      groups.set(groupKey, { displayName, seriesList: [s] });
    }
  }

  const priorityMap = buildPriorityMap(statePriority);

  const result: GroupedSeriesRow[] = [];

  for (const group of groups.values()) {
    const { displayName, seriesList: groupSeriesList } = group;
    const sorted = priorityMap ? sortByPriority(groupSeriesList, colorBy, priorityMap) : groupSeriesList;
    const firstSeries = sorted[0];
    if (!firstSeries) continue;

    const numTimestamps = firstSeries.values.length;
    const mergedValues: TimeSeriesValueTuple[] = new Array(numTimestamps);
    const cellStates: Array<string | undefined> = new Array(numTimestamps);

    const mergedLabels: Record<string, string> = {};
    for (const s of sorted) {
      if (s.labels) {
        for (const [k, v] of Object.entries(s.labels)) {
          if (k !== colorBy) {
            mergedLabels[k] = v;
          }
        }
      }
    }

    for (let i = 0; i < numTimestamps; i++) {
      let foundValue: TimeSeriesValueTuple | undefined;
      let foundState: string | undefined;

      for (const s of sorted) {
        const val = s.values[i];
        if (val && val[1] !== null) {
          foundValue = val;
          foundState = s.labels?.[colorBy];
          break;
        }
      }

      mergedValues[i] = foundValue ?? firstSeries.values[i] ?? [0, null];
      cellStates[i] = foundState;
    }

    result.push({
      name: displayName,
      labels: mergedLabels,
      values: mergedValues,
      cellStates,
    });
  }

  return result;
}

function buildPriorityMap(statePriority: string[] | undefined): Map<string, number> | undefined {
  if (!statePriority || statePriority.length === 0) return undefined;
  const map = new Map<string, number>();
  statePriority.forEach((state, index) => map.set(state, index));
  return map;
}

function sortByPriority(
  seriesList: SeriesWithLabels[],
  colorBy: string,
  priorityMap: Map<string, number>
): SeriesWithLabels[] {
  const fallback = priorityMap.size;
  return [...seriesList].sort((a, b) => {
    const aP = priorityMap.get(a.labels?.[colorBy] ?? '') ?? fallback;
    const bP = priorityMap.get(b.labels?.[colorBy] ?? '') ?? fallback;
    return aP - bP;
  });
}

/** React hook wrapper around {@link groupSeries} with memoization. */
export function useGroupedSeries(
  series: SeriesWithLabels[] | undefined,
  colorBy: string,
  statePriority?: string[]
): GroupedSeriesRow[] | undefined {
  return useMemo(() => {
    if (!series || series.length === 0) return undefined;
    return groupSeries(series, colorBy, statePriority);
  }, [series, colorBy, statePriority]);
}
